import type { Bookmark } from "./bookmarks";
import type { FS, FSConfig, FSType, GDriveConfig, LocalConfig } from "./fs";
import {
  fsConfigEq,
  fsConfigFullName,
  fsFullDir,
  fsTypeAzureBlob,
  fsTypeAzureFiles,
  fsTypeBackBlaze,
  fsTypeBox,
  fsTypeCloudinary,
  fsTypeDrime,
  fsTypeDropbox,
  fsTypeFichier,
  fsTypeFilelu,
  fsTypeFilesCom,
  fsTypeFromFSConfig,
  fsTypeFromID,
  fsTypeFtp,
  fsTypeGcs,
  fsTypeGDrive,
  fsTypeGofile,
  fsTypeHttp,
  fsTypeImagekit,
  fsTypeInternetArchive,
  fsTypeJottacloud,
  fsTypeKoofr,
  fsTypeLinkbox,
  fsTypeLocal,
  fsTypeMega,
  fsTypeOneDrive,
  fsTypeOpendrive,
  fsTypePcloud,
  fsTypePixeldrain,
  fsTypePremiumize,
  fsTypePutio,
  fsTypeQuatrix,
  fsTypeS3,
  fsTypeSeafile,
  fsTypeSftp,
  fsTypeSmb,
  fsTypeStorj,
  fsTypeSwift,
  fsTypeTestFiles,
  fsTypeUlozto,
  fsTypeWebdav,
  fsTypeYandex,
  setFsConfigStore,
} from "./fs";
import { fsTypeNeedsSignIn, makeFileSystemFromConfig, signInFsName } from "./fs-make";
import { loadLocalHandle, saveLocalHandle } from "./local-handles";
import { len } from "./util";

let lsKeyFSConfigs = "fm-fsconfigs";

function readInitialFSConfigs(): FSConfig[] {
  let v = localStorage.getItem(lsKeyFSConfigs) || "[]";
  let res: FSConfig[] = JSON.parse(v);
  console.log("initialFSConfigs:", res);
  return res;
}

// keeps a list of mounted file system so that we don't
// re-read directories on every mount
export let mountedFileSystems: FS[] = $state([]);

export let savedFsConfigs: FSConfig[] = $state(readInitialFSConfigs());

function rememberFsConfig(c: FSConfig) {
  let fsType = fsTypeFromFSConfig(c);
  switch (fsType) {
    case fsTypeS3:
    case fsTypeBackBlaze:
    case fsTypePutio:
    case fsTypePixeldrain:
    case fsTypeStorj:
    case fsTypeSftp:
    case fsTypeFtp:
    case fsTypeSmb:
    case fsTypeWebdav:
    case fsTypeBox:
    case fsTypeAzureBlob:
    case fsTypeGcs:
    case fsTypePcloud:
    case fsTypeMega:
    case fsTypeKoofr:
    case fsTypeSeafile:
    case fsTypeYandex:
    case fsTypeJottacloud:
    case fsTypeSwift:
    case fsTypeFichier:
    case fsTypeOpendrive:
    case fsTypeGofile:
    case fsTypePremiumize:
    case fsTypeInternetArchive:
    case fsTypeAzureFiles:
    case fsTypeHttp:
    case fsTypeFilesCom:
    case fsTypeFilelu:
    case fsTypeImagekit:
    case fsTypeCloudinary:
    case fsTypeDrime:
    case fsTypeQuatrix:
    case fsTypeLinkbox:
    case fsTypeUlozto:
      break;
    default:
      return;
    // we don't remember these:
    // fsTypeTestFiles : obvious reasons
    // fsTypeDropbox : prefer to re-auth for each session
    // fsTypeOneDrive : prefer to re-auth for each session
    // fsTypeGDrive : access token expires; re-auth for each session
    // fsTypeLocal : remembered by rememberLocalFolder(), its handle cannot
    //    be written to local storage
  }
  for (let c2 of savedFsConfigs) {
    if (fsConfigEq(c, c2)) {
      return;
    }
  }
  console.log("rememberFsConfig:", c);
  savedFsConfigs.push(c);
  let v = JSON.stringify(savedFsConfigs);
  console.log("saving fsConfigs:", v);
  localStorage.setItem(lsKeyFSConfigs, v);
}

// Remembers a local folder in the saved connections: its handle in
// IndexedDB, its id and name with the other connections. If the folder is
// saved already, the config gets the id of that connection, so that the two
// are one connection. Call it before the config is used for a file system.
export async function rememberLocalFolder(c: LocalConfig): Promise<void> {
  try {
    for (let saved of savedFsConfigs) {
      if (fsTypeFromFSConfig(saved) != fsTypeLocal) {
        continue;
      }
      let h = await loadLocalHandle(saved.fsID);
      if (h && (await h.isSameEntry(c.dirHandle))) {
        c.fsID = saved.fsID;
        return;
      }
    }
    await saveLocalHandle(c.fsID, c.dirHandle);
  } catch (e) {
    // a browser without IndexedDB, or in a private window: the folder works,
    // it is only not remembered
    console.error("rememberLocalFolder:", e);
    return;
  }
  // without the handle
  savedFsConfigs.push({ fsID: c.fsID, name: c.name } as LocalConfig);
  localStorage.setItem(lsKeyFSConfigs, JSON.stringify(savedFsConfigs));
}

setFsConfigStore({
  load(c: FSConfig): FSConfig | null {
    let saved: FSConfig[] = [];
    try {
      saved = JSON.parse(localStorage.getItem(lsKeyFSConfigs) || "[]");
    } catch {
      return null;
    }
    return saved.find((c2) => fsConfigEq(c, c2)) || null;
  },
  save(c: FSConfig) {
    let saved = savedFsConfigs.find((c2) => fsConfigEq(c, c2));
    if (!saved) {
      return;
    }
    // the saved copy keeps its id and name
    let { fsID: _fsID, name: _name, ...rest } = $state.snapshot(c) as FSConfig;
    Object.assign(saved, rest);
    localStorage.setItem(lsKeyFSConfigs, JSON.stringify(savedFsConfigs));
  },
});

export function rememberFileSystem(fs: FS) {
  rememberFsConfig(fs.config);
  // don't remember multipe connections for the same fs
  let conf = fs.config;
  for (let existingFS of mountedFileSystems) {
    let conf2 = existingFS.config;
    if (fsConfigEq(conf, conf2)) {
      if (fs.type == fsTypeGDrive) {
        let prev = conf2 as GDriveConfig;
        let next = conf as GDriveConfig;
        if (prev.email != next.email) {
          existingFS.dirs.clear();
          existingFS.filesCount = 0;
        }
        prev.accessToken = next.accessToken;
        prev.tokenExpires = next.tokenExpires;
        prev.email = next.email;
        if (next.name) {
          prev.name = next.name;
        }
      }
      console.log("rememberFileSystem: already fs with config", fs.config, existingFS.config);
      return;
    }
  }
  console.log("rememberFileSystem: new fs", fs.config);
  mountedFileSystems.push(fs);
}

const lsKeyBookmarks = "fm-bookmarks";

function initialBookmarks(): Bookmark[] {
  let sv = localStorage.getItem(lsKeyBookmarks) || "[]";
  let res: Bookmark[] = JSON.parse(sv);
  console.log("initialBookmarks:", res);
  return res;
}

export function getStoredBookmarks(): Bookmark[] {
  return initialBookmarks();
}

export let bookmarks: Bookmark[] = $state(initialBookmarks());

function shouldStoreBookmark(b: Bookmark): boolean {
  let fsType = bookmarkFsType(b);
  switch (fsType) {
    // gone with the session
    case fsTypeTestFiles:
      return false;
    // kept if the folder is a saved connection
    case fsTypeLocal:
      return savedFsConfigs.some((c) => c.fsID == b.fsID);
  }

  return true;
}

// $effect.root() needed for $effect() outside of a component
$effect.root(() => {
  $effect(() => {
    let stored = [];
    for (let b of bookmarks) {
      if (shouldStoreBookmark(b)) {
        stored.push(b);
      }
    }
    let v = JSON.stringify(stored);
    console.log("saving bookmarks:", stored);
    localStorage.setItem(lsKeyBookmarks, v);
  });
  return () => {
    console.log("cleanup of root effect for saving bookmarks");
  };
});

export function bookmarkEq(b1: Bookmark, b2: Bookmark): boolean {
  if (b1.fsID != b2.fsID) {
    return false;
  }
  if (b1.dir != b2.dir) {
    return false;
  }
  return true;
}

export function removeBookmark(b: Bookmark) {
  console.log("removeBookmark:", b);
  function removeBookmarkFromArray(a: Bookmark[], b: Bookmark) {
    let n = len(a);
    for (let i = 0; i < n; i++) {
      let b2 = a[i];
      if (bookmarkEq(b, b2)) {
        a.splice(i, 1);
        return;
      }
    }
    console.log("didn't remove bookmark:", b);
  }
  removeBookmarkFromArray(bookmarks, b);
}

export function bookmarkExists(b: Bookmark): boolean {
  for (let b2 of bookmarks) {
    if (bookmarkEq(b, b2)) {
      return true;
    }
  }
  return false;
}

export function addBookmark(b: Bookmark) {
  if (bookmarkExists(b)) {
    return;
  }
  bookmarks.push({ fsID: b.fsID, dir: b.dir });
}

// The id bookmarks of fs are kept under. A connection made again in a dialog
// gets a new id while the saved one keeps the id it had, so the id of the
// saved connection is the one that lasts.
export function bookmarkFsID(fs: FS): string {
  let saved = savedFsConfigs.find((c) => fsConfigEq(c, fs.config));
  return saved ? saved.fsID : fs.config.fsID;
}

export function bookmarkForFS(fs: FS, dir: string): Bookmark {
  return { fsID: bookmarkFsID(fs), dir };
}

// Returns the file system of a bookmark: a connected one, or one made from
// the saved connection. null if the bookmark is of a connection that is
// neither connected nor saved.
export function findBookmarkFS(b: Bookmark): FS | null {
  for (let fs of mountedFileSystems) {
    if (fs.config.fsID == b.fsID || bookmarkFsID(fs) == b.fsID) {
      return fs;
    }
  }
  let saved = savedFsConfigs.find((c) => c.fsID == b.fsID);
  if (!saved || fsTypeFromFSConfig(saved) == fsTypeLocal) {
    // a saved local folder is opened with openSavedLocalFolder(): it has to
    // ask for permission
    return null;
  }
  try {
    return makeFileSystemFromConfig(saved);
  } catch (e) {
    console.error(e);
    return null;
  }
}

// Returns the saved connection of a local folder a bookmark is in, if any.
export function findBookmarkSavedLocal(b: Bookmark): LocalConfig | null {
  let saved = savedFsConfigs.find((c) => c.fsID == b.fsID);
  if (!saved || fsTypeFromFSConfig(saved) != fsTypeLocal) {
    return null;
  }
  return saved as LocalConfig;
}

// What a bookmark is shown as, e.g. "pixeldrain:/photos".
export function bookmarkLabel(b: Bookmark): string {
  for (let fs of mountedFileSystems) {
    if (fs.config.fsID == b.fsID || bookmarkFsID(fs) == b.fsID) {
      return fsFullDir(fs, b.dir);
    }
  }
  let saved = savedFsConfigs.find((c) => c.fsID == b.fsID);
  if (saved) {
    return (saved.name || fsConfigFullName(saved)) + ":" + b.dir;
  }
  let fsType = bookmarkFsType(b);
  if (fsType && fsTypeNeedsSignIn(fsType)) {
    return signInFsName(fsType) + ":" + b.dir;
  }
  return b.fsID + ":" + b.dir;
}

// The type of the file system of a bookmark, null for an id we do not know.
export function bookmarkFsType(b: Bookmark): FSType | null {
  try {
    return fsTypeFromID(b.fsID);
  } catch {
    return null;
  }
}
